import { useState, type ReactNode } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { apiPut } from '@/api/axios-client'
import { getErrorMessage } from '@/api/errors'
import { qk } from '@/api/query-keys'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { SearchSelect } from '@/components/composites/search-select'
import { cn } from '@/lib/utils'
import { newRowId, type Metric } from '@/features/projects'
import { lifecycleApi, useLifecycleWrite } from './api'
import {
  PHASE_LABEL,
  PRODUCTS,
  PRODUCT_STATUSES,
  PRODUCT_STATUS_LABEL,
  dayOf,
  isoOfDay,
  longDate,
  type ContactRow,
  type Dashboard,
  type ProductRow,
  type ProductStatus,
  type ScopeRow,
} from './model'

type People = Array<{ id: string; label: string; hint?: string; photoUrl?: string | null }>

function Shell({ title, description, children, footer, onClose, busy, wide }: { title: string; description?: string; children: ReactNode; footer: ReactNode; onClose: () => void; busy: boolean; wide?: boolean }) {
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className={cn('max-h-[92dvh] overflow-y-auto', wide ? 'sm:max-w-3xl' : 'sm:max-w-lg')}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>
        <div className="grid gap-3">{children}</div>
        <DialogFooter className="gap-2">{footer}</DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/* ------------------------------------------------------------------ exit gate */

export function GateDialog({ data, onClose }: { data: Dashboard; onClose: () => void }) {
  const lc = data.lifecycle
  const phase = lc.currentPhase
  const items = data.gates[phase] ?? []
  const [checks, setChecks] = useState<string[]>([])
  const [decision, setDecision] = useState<'go' | 'no_go' | null>(null)
  const [note, setNote] = useState('')
  const [carry, setCarry] = useState(false)
  const cycle = phase === 'post_sales' ? 1 : lc.currentCycle
  const open = data.tasks.filter((t) => t.phase === phase && t.cycle === cycle && t.status !== 'done')
  const advance = useLifecycleWrite(data.project.id, () => lifecycleApi.advance(data.project.id, { checks, decision, note: note.trim(), carryOpenTasks: carry }), phase === 'offboarding' ? 'Client Project closed' : `${PHASE_LABEL[phase]} gate passed`)
  const ready = items.every((g) => checks.includes(g)) && (phase !== 'pilot' || decision !== null) && (open.length === 0 || carry)
  const next = phase === 'offboarding' ? 'The project closes.' : phase === 'pilot' && decision === 'no_go' ? 'A no-go moves the project to Offboarding.' : phase === 'hypercare' ? 'The project returns to Post-Sales.' : 'The next phase starts today, and later phases move with it.'

  return (
    <Shell
      title={phase === 'offboarding' ? 'Close the Client Project' : `${PHASE_LABEL[phase]} exit gate`}
      description="The phase moves on only when every condition is true."
      onClose={onClose}
      busy={advance.isPending}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={advance.isPending}>Cancel</Button>
          <Button onClick={() => advance.mutate(undefined, { onSuccess: onClose })} disabled={!ready || advance.isPending}>{advance.isPending ? 'Saving…' : phase === 'offboarding' ? 'Close project' : 'Gate met — move on'}</Button>
        </>
      }
    >
      <ul className="grid gap-2">
        {items.map((g) => (
          <li key={g}>
            <label className="flex items-start gap-2 text-sm">
              <Checkbox className="mt-0.5" checked={checks.includes(g)} onCheckedChange={(v) => setChecks((c) => (v === true ? [...c, g] : c.filter((x) => x !== g)))} />
              {g}
            </label>
          </li>
        ))}
      </ul>
      {phase === 'pilot' ? (
        <div className="grid gap-1.5">
          <Label>Go / no-go</Label>
          <div className="flex gap-2">
            {(['go', 'no_go'] as const).map((d) => (
              <Button key={d} type="button" size="sm" variant={decision === d ? 'default' : 'outline'} onClick={() => setDecision(d)}>{d === 'go' ? 'Go' : 'No-go'}</Button>
            ))}
          </div>
        </div>
      ) : null}
      {open.length ? (
        <label className="flex items-start gap-2 rounded-lg bg-warning-soft px-3 py-2 text-sm text-warning">
          <Checkbox className="mt-0.5" checked={carry} onCheckedChange={(v) => setCarry(v === true)} />
          <span>{open.length} task{open.length === 1 ? ' is' : 's are'} still open in this phase. Carry {open.length === 1 ? 'it' : 'them'} over and move on anyway.</span>
        </label>
      ) : null}
      <div className="grid gap-1.5">
        <Label htmlFor="gate-note">Note (optional)</Label>
        <Textarea id="gate-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      <p className="text-xs text-muted-foreground">{next}</p>
    </Shell>
  )
}

/* ------------------------------------------------------------------ cycles, pilot extension, offboarding, escalation */

export function CycleDialog({ data, onClose }: { data: Dashboard; onClose: () => void }) {
  const n = Math.max(1, ...data.lifecycle.cycles.map((c) => c.n)) + 1
  const [name, setName] = useState('')
  const [scope, setScope] = useState('')
  const [skipPilot, setSkipPilot] = useState(false)
  const [products, setProducts] = useState<string[]>([])
  const open = useLifecycleWrite(data.project.id, () => lifecycleApi.openCycle(data.project.id, { name: name.trim() || `Cycle ${n}`, scope: scope.trim(), skipPilot, products }), `Cycle ${n} opened`)
  return (
    <Shell
      title={`Open cycle ${n}`}
      description="A new product, feature or cost improvement runs inside this project: scoping, Pilot (optional), Implementation and Hypercare, then back to Post-Sales."
      onClose={onClose}
      busy={open.isPending}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={open.isPending}>Cancel</Button>
          <Button onClick={() => open.mutate(undefined, { onSuccess: onClose })} disabled={!name.trim() || open.isPending}>{open.isPending ? 'Opening…' : 'Open cycle'}</Button>
        </>
      }
    >
      <div className="grid gap-1.5">
        <Label htmlFor="cycle-name">Name</Label>
        <Input id="cycle-name" placeholder="e.g. ATS integration" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="cycle-scope">Scope</Label>
        <Textarea id="cycle-scope" rows={3} value={scope} onChange={(e) => setScope(e.target.value)} />
      </div>
      <div className="grid gap-1.5">
        <Label>Products in this cycle</Label>
        <div className="grid gap-1.5 sm:grid-cols-2">
          {PRODUCTS.map((p) => (
            <label key={p.key} className="flex items-center gap-2 text-sm">
              <Checkbox checked={products.includes(p.key)} onCheckedChange={(v) => setProducts((c) => (v === true ? [...c, p.key] : c.filter((x) => x !== p.key)))} />
              {p.label}
            </label>
          ))}
        </div>
        <p className="text-tiny text-text-muted">Each adds its set-up, training and go-live tasks to this cycle.</p>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <Switch checked={skipPilot} onCheckedChange={setSkipPilot} />
        Skip the pilot (the client already uses the product)
      </label>
    </Shell>
  )
}

export function ExtendPilotDialog({ data, onClose }: { data: Dashboard; onClose: () => void }) {
  const [days, setDays] = useState('7')
  const [reason, setReason] = useState('')
  const extend = useLifecycleWrite(data.project.id, () => lifecycleApi.extendPilot(data.project.id, { days: Number(days), reason: reason.trim() }), 'Pilot extended')
  return (
    <Shell
      title="Extend the pilot"
      description="One extension is allowed, at most two weeks, with a revised end date in writing. Later phases and the target Post-Sales date move with it."
      onClose={onClose}
      busy={extend.isPending}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={extend.isPending}>Cancel</Button>
          <Button onClick={() => extend.mutate(undefined, { onSuccess: onClose })} disabled={!reason.trim() || extend.isPending}>{extend.isPending ? 'Saving…' : 'Extend pilot'}</Button>
        </>
      }
    >
      <div className="grid gap-1.5">
        <Label>Extension</Label>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger aria-label="Extension"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[3, 5, 7, 10, 14].map((d) => <SelectItem key={d} value={String(d)}>{d === 7 ? '1 week' : d === 14 ? '2 weeks' : `${d} days`}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="ext-reason">Reason, as agreed with the client</Label>
        <Textarea id="ext-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
      </div>
    </Shell>
  )
}

export function OffboardDialog({ data, onClose }: { data: Dashboard; onClose: () => void }) {
  const [note, setNote] = useState('')
  const go = useLifecycleWrite(data.project.id, () => lifecycleApi.offboard(data.project.id, { note: note.trim() }), 'Offboarding started')
  return (
    <Shell
      title="Start offboarding"
      description="When the client gives notice. The project moves to Offboarding (about 14 days) and its checklist is added: data, access, final invoice, exit survey."
      onClose={onClose}
      busy={go.isPending}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={go.isPending}>Cancel</Button>
          <Button variant="destructive" onClick={() => go.mutate(undefined, { onSuccess: onClose })} disabled={!note.trim() || go.isPending}>{go.isPending ? 'Saving…' : 'Start offboarding'}</Button>
        </>
      }
    >
      <div className="grid gap-1.5">
        <Label htmlFor="off-note">Exit notice</Label>
        <Textarea id="off-note" rows={3} placeholder="When and how the client gave notice, and the plan." value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
    </Shell>
  )
}

export function EscalateDialog({ data, onClose }: { data: Dashboard; onClose: () => void }) {
  const [note, setNote] = useState('')
  const go = useLifecycleWrite(data.project.id, () => lifecycleApi.escalate(data.project.id, note.trim()), 'Escalation recorded')
  return (
    <Shell
      title="Escalate to the Head of Customer Success"
      description="A Red project is escalated within 24 hours with a written recovery plan. This records that you did."
      onClose={onClose}
      busy={go.isPending}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={go.isPending}>Cancel</Button>
          <Button onClick={() => go.mutate(undefined, { onSuccess: onClose })} disabled={!note.trim() || go.isPending}>{go.isPending ? 'Saving…' : 'Record escalation'}</Button>
        </>
      }
    >
      <div className="grid gap-1.5">
        <Label htmlFor="esc-note">Recovery plan</Label>
        <Textarea id="esc-note" rows={5} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
    </Shell>
  )
}

/* ------------------------------------------------------------------ edit details */

const TABS = ['details', 'products', 'stakeholders', 'team', 'scope', 'dates'] as const
type Tab = (typeof TABS)[number]
const TAB_LABEL: Record<Tab, string> = { details: 'Details', products: 'Products', stakeholders: 'Stakeholders', team: 'Team', scope: 'Scope', dates: 'Phase dates' }

/** Everything stays editable; dates, scope and products re-flow the registry, and every change is logged. */
export function EditLifecycleDialog({ data, people, onClose, initialTab = 'details' }: { data: Dashboard; people: People; onClose: () => void; initialTab?: Tab }) {
  const lc = data.lifecycle
  const [tab, setTab] = useState<Tab>(initialTab)
  const [problem, setProblem] = useState(lc.problem ?? '')
  const [requirements, setRequirements] = useState(lc.requirements ?? '')
  const [target, setTarget] = useState(dayOf(lc.targetPostSalesAt))
  const [renewal, setRenewal] = useState(dayOf(lc.renewalAt))
  const [products, setProducts] = useState<ProductRow[]>(lc.products)
  const [contacts, setContacts] = useState<ContactRow[]>(lc.contacts)
  const [ops, setOps] = useState(lc.team.ops)
  const [pe, setPe] = useState(lc.team.pe)
  const [scope, setScope] = useState<ScopeRow[]>(lc.scope)
  const editable = lc.phases.filter((p) => p.status === 'active' || p.status === 'upcoming')
  const [dates, setDates] = useState(() => Object.fromEntries(editable.map((p) => [p.id, { start: dayOf(p.startAt), end: dayOf(p.endAt) }])))
  const [error, setError] = useState<string | null>(null)
  const save = useLifecycleWrite(data.project.id, (body: Record<string, unknown>) => lifecycleApi.patch(data.project.id, body), 'Client Project updated')

  const submit = () => {
    const body: Record<string, unknown> = {}
    if (problem.trim() !== (lc.problem ?? '')) body.problem = problem.trim()
    if (requirements.trim() !== (lc.requirements ?? '')) body.requirements = requirements.trim()
    if (target !== dayOf(lc.targetPostSalesAt)) body.targetPostSalesAt = target ? isoOfDay(target) : null
    if (renewal !== dayOf(lc.renewalAt)) body.renewalAt = renewal ? isoOfDay(renewal) : null
    if (JSON.stringify(products) !== JSON.stringify(lc.products)) {
      if (!products.length) return setError('Keep at least one product.')
      body.products = products
    }
    const cleanContacts = contacts.filter((c) => c.name.trim())
    if (JSON.stringify(cleanContacts) !== JSON.stringify(lc.contacts)) {
      if (!cleanContacts.length) return setError('Keep at least one client stakeholder.')
      body.contacts = cleanContacts.map((c) => ({ ...c, name: c.name.trim(), email: c.email.trim() }))
    }
    if (JSON.stringify({ ops, pe }) !== JSON.stringify(lc.team)) body.team = { ops, pe }
    const cleanScope = scope.filter((s) => s.text.trim())
    if (JSON.stringify(cleanScope) !== JSON.stringify(lc.scope)) body.scope = cleanScope
    const phaseEdits = editable
      .filter((p) => dates[p.id] && (dates[p.id]!.start !== dayOf(p.startAt) || dates[p.id]!.end !== dayOf(p.endAt)))
      .map((p) => ({ id: p.id, startAt: isoOfDay(dates[p.id]!.start), endAt: p.phase === 'post_sales' || !dates[p.id]!.end ? null : isoOfDay(dates[p.id]!.end) }))
    if (phaseEdits.some((p) => p.endAt && p.endAt < p.startAt)) return setError('A phase cannot end before it starts.')
    if (phaseEdits.length) body.phases = phaseEdits
    if (!Object.keys(body).length) return onClose()
    setError(null)
    save.mutate(body, { onSuccess: onClose })
  }

  return (
    <Shell
      wide
      title="Edit Client Project"
      description="Changing dates, scope or products re-flows the Gantt and the template tasks. Every change is written to the change log."
      onClose={onClose}
      busy={save.isPending}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>Cancel</Button>
          <Button onClick={submit} disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save changes'}</Button>
        </>
      }
    >
      <div className="flex flex-wrap gap-1 border-b border-border">
        {TABS.map((t) => (
          <button key={t} type="button" aria-pressed={tab === t} onClick={() => setTab(t)} className={cn('-mb-px border-b-2 px-2 pb-1.5 text-sm', tab === t ? 'border-brand font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground')}>
            {TAB_LABEL[t]}
          </button>
        ))}
      </div>
      {tab === 'details' ? (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="grid gap-1.5"><Label htmlFor="ed-target">Target Post-Sales date</Label><Input id="ed-target" type="date" value={target} onChange={(e) => setTarget(e.target.value)} /></div>
            <div className="grid gap-1.5"><Label htmlFor="ed-renewal">Renewal date</Label><Input id="ed-renewal" type="date" value={renewal} onChange={(e) => setRenewal(e.target.value)} /></div>
          </div>
          <div className="grid gap-1.5"><Label htmlFor="ed-problem">Problem statement</Label><Textarea id="ed-problem" rows={3} value={problem} onChange={(e) => setProblem(e.target.value)} /></div>
          <div className="grid gap-1.5"><Label htmlFor="ed-req">Requirements detail</Label><Textarea id="ed-req" rows={5} value={requirements} onChange={(e) => setRequirements(e.target.value)} /></div>
        </>
      ) : null}
      {tab === 'products' ? (
        <div className="grid gap-2 sm:grid-cols-2">
          {PRODUCTS.map((p) => {
            const row = products.find((x) => x.key === p.key)
            return (
              <div key={p.key} className={cn('flex items-center justify-between gap-2 rounded-lg border px-3 py-2', row ? 'border-brand bg-brand-softer' : 'border-border')}>
                <label className="flex items-center gap-2 text-sm font-medium">
                  <Checkbox checked={Boolean(row)} onCheckedChange={(v) => setProducts((c) => (v === true ? [...c, { key: p.key, status: 'proposed' }] : c.filter((x) => x.key !== p.key)))} />
                  {p.label}
                </label>
                {row ? (
                  <Select value={row.status} onValueChange={(v) => setProducts((c) => c.map((x) => (x.key === p.key ? { ...x, status: v as ProductStatus } : x)))}>
                    <SelectTrigger className="h-7 w-32 text-xs" aria-label={`${p.label} status`}><SelectValue /></SelectTrigger>
                    <SelectContent>{PRODUCT_STATUSES.map((s) => <SelectItem key={s} value={s}>{PRODUCT_STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
                  </Select>
                ) : null}
              </div>
            )
          })}
          <p className="text-tiny text-text-muted sm:col-span-2">Adding a product adds its tasks to the cycle still before go-live; removing one archives its tasks that have not started.</p>
        </div>
      ) : null}
      {tab === 'stakeholders' ? (
        <div className="grid gap-2">
          {contacts.map((c, i) => (
            <div key={c.id} className="grid gap-2 rounded-lg border border-border p-2.5 sm:grid-cols-[1fr_1fr_1.2fr_auto]">
              <Input aria-label="Name" placeholder="Name" value={c.name} onChange={(e) => setContacts((x) => x.map((y, j) => (j === i ? { ...y, name: e.target.value } : y)))} />
              <Input aria-label="Role" placeholder="Role" value={c.role} onChange={(e) => setContacts((x) => x.map((y, j) => (j === i ? { ...y, role: e.target.value } : y)))} />
              <Input aria-label="Email" placeholder="Email" value={c.email} onChange={(e) => setContacts((x) => x.map((y, j) => (j === i ? { ...y, email: e.target.value } : y)))} />
              <button type="button" aria-label="Remove stakeholder" className="grid size-9 place-items-center rounded-md text-muted-foreground hover:text-danger" onClick={() => setContacts((x) => x.filter((_, j) => j !== i))}><Trash2 className="size-4" /></button>
              <div className="flex flex-wrap gap-4 sm:col-span-4">
                {(['decisionMaker', 'sponsor', 'left'] as const).map((k) => (
                  <label key={k} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={c[k]} onCheckedChange={(v) => setContacts((x) => x.map((y, j) => (j === i ? { ...y, [k]: v === true } : y)))} />
                    {k === 'decisionMaker' ? 'Decision-maker' : k === 'sponsor' ? 'Sponsor' : 'Has left the client'}
                  </label>
                ))}
              </div>
            </div>
          ))}
          <Button type="button" variant="ghost" size="sm" className="justify-self-start text-brand" onClick={() => setContacts((x) => [...x, { id: newRowId(), name: '', role: '', email: '', decisionMaker: false, sponsor: false, left: false }])}><Plus className="size-3.5" /> Add a stakeholder</Button>
        </div>
      ) : null}
      {tab === 'team' ? (
        <>
          <div className="grid gap-1.5"><Label>Operations & Support</Label><SearchSelect multiple value={ops} onChange={setOps} options={people} placeholder="Search people…" /></div>
          <div className="grid gap-1.5"><Label>Product & Engineering</Label><SearchSelect multiple value={pe} onChange={setPe} options={people} placeholder="Search people…" /></div>
        </>
      ) : null}
      {tab === 'scope' ? (
        <div className="grid gap-2">
          {scope.map((s, i) => (
            <div key={s.id} className="grid items-center gap-2 sm:grid-cols-[1fr_auto_auto_auto]">
              <Input aria-label="Scope item" value={s.text} onChange={(e) => setScope((x) => x.map((y, j) => (j === i ? { ...y, text: e.target.value } : y)))} />
              <Select value={s.inScope ? 'in' : 'out'} onValueChange={(v) => setScope((x) => x.map((y, j) => (j === i ? { ...y, inScope: v === 'in', addTask: v === 'in' ? y.addTask : false } : y)))}>
                <SelectTrigger className="h-9 w-32" aria-label="In or out of scope"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="in">In scope</SelectItem><SelectItem value="out">Out of scope</SelectItem></SelectContent>
              </Select>
              <label className={cn('flex items-center gap-2 text-sm', !s.inScope && 'opacity-50')}>
                <Checkbox checked={s.addTask} disabled={!s.inScope} onCheckedChange={(v) => setScope((x) => x.map((y, j) => (j === i ? { ...y, addTask: v === true } : y)))} />
                Add task
              </label>
              <button type="button" aria-label="Remove scope item" className="grid size-9 place-items-center rounded-md text-muted-foreground hover:text-danger" onClick={() => setScope((x) => x.filter((_, j) => j !== i))}><Trash2 className="size-4" /></button>
            </div>
          ))}
          <Button type="button" variant="ghost" size="sm" className="justify-self-start text-brand" onClick={() => setScope((x) => [...x, { id: newRowId(), text: '', inScope: true, addTask: true }])}><Plus className="size-3.5" /> Add a scope item</Button>
        </div>
      ) : null}
      {tab === 'dates' ? (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-[30rem] text-sm">
            <thead className="bg-surface-sunken text-left text-tiny uppercase tracking-wide text-text-muted"><tr><th className="px-3 py-2">Phase</th><th className="px-3 py-2">Start</th><th className="px-3 py-2">End</th></tr></thead>
            <tbody>
              {editable.map((p) => (
                <tr key={p.id} className="border-t border-border-soft">
                  <td className="px-3 py-1.5 font-medium">{PHASE_LABEL[p.phase]}{p.cycle > 1 && p.phase !== 'post_sales' ? ` · C${p.cycle}` : ''}</td>
                  <td className="px-3 py-1.5"><Input aria-label="Start" type="date" className="h-8" value={dates[p.id]?.start ?? ''} onChange={(e) => setDates((d) => ({ ...d, [p.id]: { start: e.target.value, end: d[p.id]?.end ?? '' } }))} /></td>
                  <td className="px-3 py-1.5">{p.phase === 'post_sales' ? <span className="text-muted-foreground">Ongoing</span> : <Input aria-label="End" type="date" className="h-8" value={dates[p.id]?.end ?? ''} onChange={(e) => setDates((d) => ({ ...d, [p.id]: { start: d[p.id]?.start ?? '', end: e.target.value } }))} />}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="px-3 py-2 text-tiny text-text-muted">Finished phases keep their dates. A moved phase re-dates its template tasks that have not started.</p>
        </div>
      ) : null}
      {error ? <p role="alert" className="text-sm text-danger">{error}</p> : null}
    </Shell>
  )
}

/* ------------------------------------------------------------------ success metrics */

function progressOf(m: Metric): number | null {
  if (m.baseline === null || m.target === null || m.current === null || m.baseline === m.target) return null
  return Math.max(0, Math.min(100, Math.round(((m.current - m.baseline) / (m.target - m.baseline)) * 100)))
}

/** Baseline, target and latest value for each metric. The AM keeps "latest" current; health reads it. */
export function MetricsEditor({ projectId, plan, canManage }: { projectId: string; plan: Dashboard['plan']; canManage: boolean }) {
  const qc = useQueryClient()
  const [latest, setLatest] = useState<Record<string, string>>(() => Object.fromEntries(plan.metrics.map((m) => [m.id, m.current === null ? '' : String(m.current)])))
  const dirty = plan.metrics.some((m) => (latest[m.id] ?? '') !== (m.current === null ? '' : String(m.current)))
  const save = useMutation({
    mutationFn: () =>
      apiPut('project-plans/' + encodeURIComponent(projectId), {
        metrics: plan.metrics.map((m) => {
          const v = (latest[m.id] ?? '').trim()
          return { ...m, current: v === '' || !Number.isFinite(Number(v)) ? null : Number(v) }
        }),
        ...(plan.version !== null ? { version: plan.version } : {}),
      }),
    retry: false,
    onSuccess: () => {
      toast.success('Latest values saved')
      void qc.invalidateQueries({ queryKey: qk.lifecycle(projectId) })
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  })

  if (!plan.metrics.length) return <p className="py-2 text-sm text-muted-foreground">No success metrics.</p>
  return (
    <div className="grid gap-2">
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[40rem] text-sm">
          <thead className="bg-surface-sunken text-left text-tiny uppercase tracking-wide text-text-muted">
            <tr><th className="px-3 py-2">Metric</th><th className="px-3 py-2">Baseline</th><th className="px-3 py-2">Target</th><th className="px-3 py-2">Latest</th><th className="px-3 py-2">Due</th><th className="px-3 py-2">Progress</th></tr>
          </thead>
          <tbody>
            {plan.metrics.map((m) => {
              const p = progressOf(m)
              return (
                <tr key={m.id} className="border-t border-border-soft">
                  <td className="px-3 py-2 font-medium">{m.name}{m.unit ? <span className="text-muted-foreground"> ({m.unit})</span> : null}</td>
                  <td className="num px-3 py-2">{m.baseline ?? '—'}</td>
                  <td className="num px-3 py-2">{m.target ?? '—'}</td>
                  <td className="px-3 py-1.5">
                    {canManage ? <Input aria-label={`Latest value of ${m.name}`} inputMode="decimal" className="h-8 w-24" value={latest[m.id] ?? ''} onChange={(e) => setLatest((l) => ({ ...l, [m.id]: e.target.value }))} /> : <span className="num">{m.current ?? '—'}</span>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">{longDate(m.dueAt ?? null)}</td>
                  <td className="px-3 py-2">
                    {p === null ? <span className="text-text-muted">—</span> : (
                      <span className="flex items-center gap-2">
                        <span className="h-1.5 w-24 overflow-hidden rounded-full bg-surface-sunken"><span className={cn('block h-full rounded-full', p >= 100 ? 'bg-success' : p >= 50 ? 'bg-brand' : 'bg-warning')} style={{ width: `${p}%` }} /></span>
                        <span className="num text-xs">{p}%</span>
                      </span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {canManage ? <Button size="sm" className="justify-self-end" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Save latest values'}</Button> : null}
      <p className="text-tiny text-text-muted">A metric under halfway at its mid-point turns the project yellow; one missed at its due date turns it red.</p>
    </div>
  )
}

